import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { usePlatforms } from '../../../../hooks/usePlatforms';
import { translate } from '../../../../utils/Common';
import { verifyAccessViewPermissions } from '../../../../config/permissions';
import PageContainer from '../../../../components/layout/PageContainer/PageContainer';
import PageHeader from '../../../../components/layout/PageHeader/PageHeader';
import ContentCard from '../../../../components/layout/ContentCard/ContentCard';
import Button from '../../../../components/common/Button/Button';
import Badge from '../../../../components/common/Badge/Badge';
import Tabs from '../../../../components/common/Tabs/Tabs';
import { ToastContainer, useToast } from '../../../../components/common/Toast';
import * as FaIcons from 'react-icons/fa';
import { FiBriefcase } from 'react-icons/fi';
import { HiOfficeBuilding } from 'react-icons/hi';
import Spinner from '../../../../components/Spinner';
import UpdateLogoModal from './components/UpdateLogoModal';
import styles from './CompanyDetail.module.css';

// Components
import OverviewTab from './components/OverviewTab';
import DoorsTab from './components/DoorsTab';
import ClientsTab from './components/ClientsTab';

// Utils
import {
  formatCompanyName,
  formatCompanyCode,
} from './utils/companyFormatters';
import {
  getCompanyLogo,
  getStatusVariant,
} from './utils/companyHelpers';

const CompanyDetail = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const permissionsPath = "/dashboard/companies";
  const canRead = verifyAccessViewPermissions(["read"], { path: permissionsPath || location.pathname });
  const canUpdate = verifyAccessViewPermissions(["update"], { path: permissionsPath || location.pathname });
  const { getCompanyById, getDoorsByCompanyCode, getAssignedMembersById, updateCompany, updateCompanyLogo } = usePlatforms();
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const [totalDoors, setTotalDoors] = useState(null);
  const [totalClients, setTotalClients] = useState(null);
  // Starts true: between the first render and the effect firing, a false
  // flag with an empty list paints the "nothing here" state for a frame,
  // which reads as the screen finding nothing and then changing its mind.
  const [loadingDoors, setLoadingDoors] = useState(true);
  // Starts true: between the first render and the effect firing, a false
  // flag with an empty list paints the "nothing here" state for a frame,
  // which reads as the screen finding nothing and then changing its mind.
  const [loadingClients, setLoadingClients] = useState(true);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const abortControllerRef = useRef(null);
  const countsAbortControllerRef = useRef(null);

  useEffect(() => {
    const loadCompany = async () => {
      setLoading(true);
      setError(null);
      setLogoError(false);
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      const liveController = new AbortController();
      abortControllerRef.current = liveController;
      
      try {
        const companyData = await getCompanyById(companyId, abortControllerRef.current.signal);
        if (companyData && companyData.length > 0) {
          setCompany(companyData[0]);
        } else {
          setError(translate('dashboard.companies.detail.errors.notFound'));
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading company:', err);
          setError(err.message || translate('dashboard.companies.detail.errors.loadError'));
        }
      } finally {
        // Only the request that was not superseded may clear the flag: an
        // aborted one used to switch it off for its replacement, blanking the
        // view until the new data arrived.
        if (!liveController.signal.aborted) setLoading(false);
      }
    };

    loadCompany();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [companyId, getCompanyById]);

  // Load counts for doors and clients
  useEffect(() => {
    if (!company) return;

    const loadCounts = async () => {
      if (countsAbortControllerRef.current) {
        countsAbortControllerRef.current.abort();
      }

      countsAbortControllerRef.current = new AbortController();
      const signal = countsAbortControllerRef.current.signal;

      // Reset counts and set loading states
      setTotalDoors(null);
      setTotalClients(null);
      setLoadingDoors(true);
      setLoadingClients(true);

      try {
        // Load doors count
        const companyCode = company?.companyCode || company?.CodUser;
        if (companyCode) {
          try {
            const doorsResult = await getDoorsByCompanyCode(companyCode, signal);
            const doorsList = Array.isArray(doorsResult) ? doorsResult : (doorsResult?.payload || doorsResult?.doors || []);
            setTotalDoors(doorsList.length);
          } catch (err) {
            if (err.name !== 'AbortError') {
              console.error('Error loading doors count:', err);
              setTotalDoors(0);
            }
          } finally {
            setLoadingDoors(false);
          }
        } else {
          setTotalDoors(0);
          setLoadingDoors(false);
        }

        // Load clients count
        try {
          const clientsResult = await getAssignedMembersById(companyId, 1, 1, signal);
          // Handle different response formats
          let total = 0;
          if (clientsResult?.total !== undefined) {
            total = clientsResult.total;
          } else if (clientsResult?.members && Array.isArray(clientsResult.members)) {
            total = clientsResult.total || clientsResult.members.length;
          } else if (Array.isArray(clientsResult)) {
            total = clientsResult.length;
          } else if (clientsResult?.payload) {
            total = clientsResult.total || clientsResult.payload.length || 0;
          }
          setTotalClients(total);
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Error loading clients count:', err);
            setTotalClients(0);
          }
        } finally {
          setLoadingClients(false);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading counts:', err);
          setLoadingDoors(false);
          setLoadingClients(false);
        }
      }
    };

    loadCounts();

    return () => {
      if (countsAbortControllerRef.current) {
        countsAbortControllerRef.current.abort();
      }
    };
  }, [company, companyId, getDoorsByCompanyCode, getAssignedMembersById]);

  const handleBack = () => {
    history.push('/dashboard/companies');
  };

  const handleBreadcrumbClick = (e) => {
    e.preventDefault();
    handleBack();
  };

  const handleUpdateCompany = async (updateData) => {
    if (!company) return;
    if (!canUpdate) return { success: false, error: translate("dashboard.clients.formSearch.NOT_ACCESS") };
    
    const companyCode = company?.companyCode || company?.CodUser;
    if (!companyCode) {
      console.error('Company code not found');
      return { success: false, error: translate('dashboard.companies.detail.errors.companyCodeNotFound') };
    }

    try {
      const abortController = new AbortController();
      const result = await updateCompany(companyCode, updateData, abortController.signal);
      
      if (result && (result.code === 200 || result.success)) {
        // Reload company data
        const companyData = await getCompanyById(companyId, abortController.signal);
        if (companyData && companyData.length > 0) {
          setCompany(companyData[0]);
        }
        return { success: true, result };
      } else {
        return { success: false, error: result?.message || translate('dashboard.companies.detail.errors.updateError') };
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error updating company:', error);
        return { success: false, error: error.message || translate('dashboard.companies.detail.errors.updateError') };
      }
      return { success: false, error: 'Request cancelled' };
    }
  };

  const handleUpdateLogo = async (logoFile) => {
    if (!company) return { success: false, error: translate('dashboard.companies.detail.errors.notFound') };
    if (!canUpdate) return { success: false, error: translate("dashboard.clients.formSearch.NOT_ACCESS") };
    
    const companyCode = company?.companyCode || company?.CodUser;
    if (!companyCode) {
      console.error('Company code not found');
      return { success: false, error: translate('dashboard.companies.detail.errors.companyCodeNotFound') };
    }

    try {
      const abortController = new AbortController();
      const result = await updateCompanyLogo(companyCode, logoFile, abortController.signal);
      
      if (result && (result.code === 200 || result.success)) {
        // Reload company data
        const companyData = await getCompanyById(companyId, abortController.signal);
        if (companyData && companyData.length > 0) {
          setCompany(companyData[0]);
          setLogoError(false); // Reset logo error state
        }
        return { success: true, result };
      } else {
        return { success: false, error: result?.message || translate('dashboard.companies.detail.errors.updateLogoError') };
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error updating logo:', error);
        return { success: false, error: error.message || translate('dashboard.companies.detail.errors.updateLogoError') };
      }
      return { success: false, error: 'Request cancelled' };
    }
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  const handleRefreshClientsCount = async () => {
    if (!company || !companyId) return;

    if (countsAbortControllerRef.current) {
      countsAbortControllerRef.current.abort();
    }

    countsAbortControllerRef.current = new AbortController();
    const signal = countsAbortControllerRef.current.signal;

    try {
      setLoadingClients(true);
      const clientsResult = await getAssignedMembersById(companyId, 1, 1, signal);
      // Handle different response formats
      let total = 0;
      if (clientsResult?.total !== undefined) {
        total = clientsResult.total;
      } else if (clientsResult?.members && Array.isArray(clientsResult.members)) {
        total = clientsResult.total || clientsResult.members.length;
      } else if (Array.isArray(clientsResult)) {
        total = clientsResult.length;
      } else if (clientsResult?.payload) {
        total = clientsResult.total || clientsResult.payload.length || 0;
      }
      setTotalClients(total);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error refreshing clients count:', err);
      }
    } finally {
      setLoadingClients(false);
    }
  };

  const tabs = [
    {
      label: translate('dashboard.companies.detail.tabs.overview'),
      icon: <FiBriefcase />,
      component: (
        <OverviewTab 
          company={company}
          onUpdateCompany={handleUpdateCompany}
          canEdit={canUpdate}
        />
      ),
      show: true,
    },
    {
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{translate('dashboard.companies.detail.tabs.doors')}</span>
          {loadingDoors ? (
            <Spinner type={1} size={12} />
          ) : (
            <Badge variant="neutral" size="sm" showIcon={false}>
              {totalDoors ?? 0}
            </Badge>
          )}
        </span>
      ),
      icon: <FaIcons.FaDoorOpen />,
      component: <DoorsTab company={company} showSuccess={showSuccess} showError={showError} />,
      show: true,
    },
    {
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{translate('dashboard.companies.detail.tabs.clients')}</span>
          {loadingClients ? (
            <Spinner type={1} size={12} />
          ) : (
            <Badge variant="neutral" size="sm" showIcon={false}>
              {totalClients ?? 0}
            </Badge>
          )}
        </span>
      ),
      icon: <FaIcons.FaUsers />,
      component: <ClientsTab company={company} showSuccess={showSuccess} showError={showError} onClientAdded={handleRefreshClientsCount} />,
      show: true,
    },
  ];

  if (loading) {
    return (
      <PageContainer>
        <div className={styles.loadingContainer}>
          <Spinner type={1} size={40} />
        </div>
      </PageContainer>
    );
  }

  if (!canRead) {
    return (
      <PageContainer>
        <PageHeader
          title={translate('global.companies')}
          icon={<HiOfficeBuilding size={28} />}
          divider={false}
        />
        <ContentCard padding="md">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(107,114,128,0.12)",
                color: "#374151",
                flex: "0 0 auto",
              }}
              aria-hidden
            >
              <FaIcons.FaLock size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
                {translate("dashboard.configuration.noPermissionTitle")}
              </div>
              <div style={{ marginTop: 4, color: "#6B7280" }}>
                {translate("dashboard.clients.formSearch.NOT_ACCESS")}
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="primary" type="button" onClick={handleBack}>
                  {translate("global.goBack")}
                </Button>
              </div>
            </div>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  if (error || !company) {
    return (
      <PageContainer>
        <ContentCard padding="md">
          <div className={styles.errorContainer}>
            <p>{error || translate('dashboard.companies.detail.errors.notFound')}</p>
            <Button onClick={handleBack} variant="primary">
              {translate('dashboard.companies.detail.actions.backToCompanies')}
            </Button>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  const isActive = company?.isActive !== undefined ? company.isActive : company?.Activo;
  const companyLogo = getCompanyLogo(company, logoError);

  return (
    <PageContainer>
      <PageHeader
        title={formatCompanyName(company)}
        breadcrumbs={[
          (
            <span
              key="companies"
              onClick={handleBreadcrumbClick}
              style={{ 
                cursor: 'pointer', 
                textDecoration: 'none',
                color: 'var(--text-secondary)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                padding: '0.125rem 0.375rem',
                margin: '0 -0.375rem',
                borderRadius: '0.25rem',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--text-primary)';
                e.target.style.backgroundColor = 'var(--bg-hover, rgba(0, 0, 0, 0.02)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--text-secondary)';
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              {translate('global.companies')}
            </span>
          ),
          formatCompanyName(company)
        ]}
        subtitle={
          <div className={styles.subtitleWithBadge}>
            <span>{translate('dashboard.companies.detail.subtitle').replace('{id}', formatCompanyCode(company, companyId)).replace('{code}', company?.companyCode || 'N/A')}</span>
            <Badge variant={getStatusVariant(isActive)} showIcon>
              {isActive ? translate('dashboard.companies.status.active') : translate('dashboard.companies.status.inactive')}
            </Badge>
          </div>
        }
        logo={
          <div className={styles.logoContainer}>
            <img 
              src={companyLogo} 
              alt={formatCompanyName(company)} 
              className={styles.companyLogo}
              onError={handleLogoError}
            />
            {canUpdate && (
              <button
                type="button"
                className={styles.logoEditButton}
                onClick={() => setIsLogoModalOpen(true)}
                aria-label={translate('dashboard.companies.detail.actions.editLogo.ariaLabel')}
              >
                <FaIcons.FaEdit size={12} />
              </button>
            )}
          </div>
        }
        actions={
          <div className={styles.headerActions}>
            <Button 
              variant="primary" 
              onClick={handleBack}
              leftIcon={<FaIcons.FaArrowLeft />}
            >
              {translate('dashboard.companies.detail.actions.backToCompanies')}
            </Button>
          </div>
        }
        divider={false}
      />

      {/* Tabs */}
      <Tabs
        items={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        lazy={false}
      />

      {/* Update Logo Modal */}
      <UpdateLogoModal
        isOpen={isLogoModalOpen}
        onClose={() => setIsLogoModalOpen(false)}
        currentLogo={companyLogo}
        onSave={handleUpdateLogo}
        companyName={formatCompanyName(company)}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </PageContainer>
  );
};

export default CompanyDetail;
